// Synchronized port of operate-ui/src/components/ContentPicker.tsx.
// Themed for Curate (curate-* tokens) and reads from the curate-api
// /api/v1/content/children endpoint via @/lib/content. If you change the
// component's props or the BrowseResult shape, update both files.
import { useEffect, useMemo, useState } from "react";
import {
  ArrowUp,
  ChevronRight,
  File,
  Folder,
  FolderOpen,
  Package,
  Search,
  Users,
  X,
} from "lucide-react";
import {
  buildBreadcrumbs,
  listContentChildren,
  parentPath,
  type ContentNode,
} from "@/lib/content";

type Props = {
  open: boolean;
  title?: string;
  initialPath?: string;
  onCancel: () => void;
  onConfirm: (picked: { path: string; name: string; objectClass: string }) => void;
};

/**
 * Two-pane content browser: breadcrumb + folder listing + manual
 * search-path fallback. Lets the user pin a Cognos folder scope without
 * hand-typing {@code /content/folder[@name='…']} XPath strings.
 */
export function ContentPicker({ open, title = "Pick a Cognos scope", initialPath, onCancel, onConfirm }: Props) {
  const [currentPath, setCurrentPath] = useState("/");
  const [children, setChildren] = useState<ContentNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ContentNode | null>(null);
  const [manualPath, setManualPath] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    setCurrentPath(initialPath || "/");
    setSelected(null);
    setManualPath(initialPath || "");
    setFilter("");
    setError(null);
  }, [open, initialPath]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listContentChildren(currentPath)
      .then((r) => { if (!cancelled) setChildren(r.children); })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setChildren([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, currentPath]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath]);
  const filtered = useMemo(() => {
    if (!filter.trim()) return children;
    const needle = filter.trim().toLowerCase();
    return children.filter((c) => c.name.toLowerCase().includes(needle));
  }, [children, filter]);

  if (!open) return null;

  function pick(n: ContentNode) {
    setSelected(n);
    setManualPath(n.path);
  }

  function openFolder(n: ContentNode) {
    if (!n.container) return;
    setCurrentPath(n.path);
    setSelected(n);
    setManualPath(n.path);
  }

  function goUp() {
    const p = parentPath(currentPath);
    if (!p) return;
    setCurrentPath(p);
    setManualPath(p);
  }

  function confirm() {
    if (selected) {
      onConfirm({ path: selected.path, name: selected.name, objectClass: selected.objectClass });
      return;
    }
    const p = manualPath.trim() || currentPath;
    if (!p || p === "/") return;
    onConfirm({ path: p, name: labelFromPath(p), objectClass: "" });
  }

  const canConfirm = (selected?.path?.length ?? 0) > 0
    || (manualPath.trim().length > 0 && manualPath.trim() !== "/");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-curate-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="flex max-h-[85vh] w-[min(920px,95vw)] flex-col overflow-hidden rounded-xl2 border border-curate-slate/25 bg-white shadow-elev">
        <header className="flex items-center justify-between border-b border-curate-slate/20 px-5 py-3">
          <h2 className="font-display text-base font-semibold text-curate-ink">{title}</h2>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md p-1 text-curate-slate hover:bg-curate-mist hover:text-curate-ink"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={goUp}
              disabled={!parentPath(currentPath)}
              className="inline-flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-xs font-medium text-curate-ink hover:border-curate-primaryDark disabled:opacity-40"
              title="Up one level"
            >
              <ArrowUp size={12} /> Up
            </button>
            <Crumb
              active={currentPath === "/"}
              onClick={() => { setCurrentPath("/"); setManualPath("/"); }}
              label="root"
            />
            {breadcrumbs.map((b) => (
              <Crumb
                key={b.path}
                active={b.path === currentPath}
                onClick={() => { setCurrentPath(b.path); setManualPath(b.path); }}
                label={b.label}
              />
            ))}
            <div className="ml-auto flex items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2 py-1">
              <Search size={13} className="text-curate-slate" aria-hidden />
              <input
                type="text"
                placeholder="Filter items…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-40 border-0 bg-transparent text-xs text-curate-ink placeholder:text-curate-slate focus:outline-none"
                aria-label="Filter items"
              />
              {filter && (
                <button
                  type="button"
                  onClick={() => setFilter("")}
                  aria-label="Clear filter"
                  className="text-curate-slate hover:text-curate-ink"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-curate-danger/30 bg-curate-danger/5 px-3 py-2 text-xs text-curate-danger">
              {error}
            </div>
          )}

          <div
            className="min-h-0 flex-1 overflow-y-auto rounded-md border border-curate-slate/20 bg-curate-mist/30"
            aria-busy={loading}
          >
            {loading ? (
              <p className="p-4 text-sm text-curate-slate">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-curate-slate">
                {children.length === 0
                  ? "No items in this folder. Paste a path below if you know it."
                  : "No items match your filter."}
              </p>
            ) : (
              <ul className="divide-y divide-curate-slate/15">
                {filtered.map((n) => {
                  const isSelected = selected?.path === n.path;
                  return (
                    <li
                      key={n.path}
                      onClick={() => pick(n)}
                      onDoubleClick={() => openFolder(n)}
                      className={`group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                        isSelected
                          ? "bg-curate-accent/15 text-curate-ink"
                          : "text-curate-ink hover:bg-white"
                      }`}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-curate-slate">
                        {iconFor(n)}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{n.name}</span>
                      <span className="text-2xs uppercase tracking-wide text-curate-slate">
                        {n.objectClass}
                      </span>
                      {n.container && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openFolder(n); }}
                          className="inline-flex items-center gap-0.5 rounded border border-curate-slate/30 bg-white px-1.5 py-0.5 text-2xs text-curate-ink opacity-0 hover:border-curate-primaryDark group-hover:opacity-100"
                        >
                          Open <ChevronRight size={10} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-curate-slate">
              Or paste a search path
            </span>
            <input
              value={manualPath}
              onChange={(e) => setManualPath(e.target.value)}
              placeholder="/content/folder[@name='GO Sales']"
              className="w-full rounded-md border border-curate-slate/30 bg-white px-2 py-1.5 text-xs text-curate-ink focus:border-curate-primaryDark focus:outline-none"
            />
          </label>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-curate-slate/20 bg-curate-mist/40 px-5 py-3">
          <div className="min-w-0 truncate text-xs text-curate-slate">
            Selected:{" "}
            <code className="rounded bg-white px-1.5 py-0.5 text-[11px] text-curate-ink">
              {selected?.path || manualPath.trim() || currentPath}
            </code>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="rounded-md border border-curate-slate/30 bg-white px-3 py-1.5 text-sm text-curate-ink hover:border-curate-primaryDark"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={!canConfirm}
              className="rounded-md bg-curate-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-curate-primaryDark disabled:cursor-not-allowed disabled:opacity-50"
            >
              Use this scope
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function iconFor(n: ContentNode) {
  const oc = n.objectClass;
  if (oc === "folder" || oc === "adminFolder") return <Folder size={14} />;
  if (oc === "package") return <Package size={14} />;
  if (oc === "namespace" || oc === "directory") return <Users size={14} />;
  if (oc === "report" || oc === "queryStudioReport" || oc === "analysis") return <File size={14} />;
  return n.container ? <FolderOpen size={14} /> : <File size={14} />;
}

function Crumb({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2 py-1 text-xs ${
        active
          ? "bg-curate-ink text-white"
          : "border border-curate-slate/25 bg-white text-curate-ink hover:border-curate-primaryDark"
      }`}
    >
      {label}
    </button>
  );
}

function labelFromPath(p: string): string {
  const bs = buildBreadcrumbs(p);
  return bs.length === 0 ? p : bs[bs.length - 1].label;
}
